/**
 * Frontend Chat Analytics
 * 
 * Lightweight tracking for Bob AI interactions
 * Sends interaction events to backend analytics system and PostHog
 */

import { posthog } from './posthog';
import { getApiUrl } from './api-config';

interface ChatEvent {
  event_type: string;
  session_id?: string | null;
  user_id?: string;
  company_id?: string;
  event_data: Record<string, any>;
  timestamp: string;
}

interface TypingPattern {
  message_length: number;
  time_to_send: number; // milliseconds
  is_follow_up: boolean;
}

class ChatAnalytics {
  private sessionId: string | null = null;
  private sessionStartTime: number | null = null;
  private messageCount: number = 0;
  private lastMessageTime: number | null = null;

  /**
   * Initialize chat session
   */
  trackChatOpen(userId?: string, companyId?: string): void {
    // Only create new session if we don't have one
    if (!this.sessionId) {
      this.sessionId = this.generateSessionId();
      this.sessionStartTime = Date.now();
      this.messageCount = 0;
      this.lastMessageTime = null;

      // Send to Supabase analytics
      this.sendEvent({
        event_type: 'session_start',
        user_id: userId,
        company_id: companyId,
        event_data: {
          session_id: this.sessionId,
          user_agent: navigator.userAgent,
          timestamp: new Date().toISOString()
        },
        timestamp: new Date().toISOString()
      });

      // Send to PostHog
      posthog.capture('ai_chat_opened', {
        session_id: this.sessionId,
        company_id: companyId,
        user_agent: navigator.userAgent,
        timestamp: new Date().toISOString()
      });
    }
  }

  /**
   * Track when user sends a message
   */
  trackMessageSent(message: string, userId?: string, companyId?: string): void {
    const now = Date.now();
    const typingPattern: TypingPattern = {
      message_length: message.length,
      time_to_send: this.lastMessageTime ? now - this.lastMessageTime : 0,
      is_follow_up: this.messageCount > 0
    };

    this.messageCount++;
    this.lastMessageTime = now;

    // Send to Supabase analytics
    this.sendEvent({
      event_type: 'message_sent',
      session_id: this.sessionId,
      user_id: userId,
      company_id: companyId,
      event_data: {
        message_length: message.length,
        message_count: this.messageCount,
        typing_pattern: typingPattern,
        timestamp: new Date().toISOString()
      },
      timestamp: new Date().toISOString()
    });

    // Send to PostHog
    posthog.capture('ai_message_sent', {
      session_id: this.sessionId,
      company_id: companyId,
      message_length: message.length,
      message_count: this.messageCount,
      typing_pattern: typingPattern,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Track when AI response is received
   */
  trackResponseReceived(responseLength: number, responseTime: number, userId?: string, companyId?: string): void {
    // Send to Supabase analytics
    this.sendEvent({
      event_type: 'response_received',
      session_id: this.sessionId,
      user_id: userId,
      company_id: companyId,
      event_data: {
        response_length: responseLength,
        response_time_ms: responseTime,
        message_count: this.messageCount,
        timestamp: new Date().toISOString()
      },
      timestamp: new Date().toISOString()
    });

    // Send to PostHog
    posthog.capture('ai_response_received', {
      session_id: this.sessionId,
      company_id: companyId,
      response_length: responseLength,
      response_time_ms: responseTime,
      message_count: this.messageCount,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Track user rating of AI response
   */
  trackRatingSubmitted(rating: number, messageId: string, userId?: string, companyId?: string): void {
    // Send to Supabase analytics
    this.sendEvent({
      event_type: 'rating_submitted',
      session_id: this.sessionId,
      user_id: userId,
      company_id: companyId,
      event_data: {
        rating,
        message_id: messageId,
        timestamp: new Date().toISOString()
      },
      timestamp: new Date().toISOString()
    });

    // Send to PostHog
    posthog.capture('ai_rating_submitted', {
      session_id: this.sessionId,
      company_id: companyId,
      rating,
      message_id: messageId,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Track chat session end
   */
  trackChatClose(userId?: string, companyId?: string): void {
    const sessionDuration = this.sessionStartTime ? Date.now() - this.sessionStartTime : 0;

    // Send to Supabase analytics
    this.sendEvent({
      event_type: 'session_end',
      session_id: this.sessionId,
      user_id: userId,
      company_id: companyId,
      event_data: {
        session_duration_ms: sessionDuration,
        total_messages: this.messageCount,
        timestamp: new Date().toISOString()
      },
      timestamp: new Date().toISOString()
    });

    // Send to PostHog
    posthog.capture('ai_session_ended', {
      session_id: this.sessionId,
      company_id: companyId,
      session_duration_ms: sessionDuration,
      total_messages: this.messageCount,
      timestamp: new Date().toISOString()
    });

    // Reset session data
    this.sessionId = null;
    this.sessionStartTime = null;
    this.messageCount = 0;
    this.lastMessageTime = null;
  }

  /**
   * Track tool usage (when AI uses tools)
   */
  trackToolUsed(toolName: string, success: boolean, userId?: string, companyId?: string): void {
    // Send to Supabase analytics
    this.sendEvent({
      event_type: 'tool_used',
      session_id: this.sessionId,
      user_id: userId,
      company_id: companyId,
      event_data: {
        tool_name: toolName,
        success,
        message_count: this.messageCount,
        timestamp: new Date().toISOString()
      },
      timestamp: new Date().toISOString()
    });

    // Send to PostHog
    posthog.capture('ai_tool_invoked', {
      session_id: this.sessionId,
      company_id: companyId,
      tool_name: toolName,
      success,
      message_count: this.messageCount,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Track user interaction patterns
   */
  trackInteraction(type: 'click' | 'hover' | 'focus', element: string, userId?: string, companyId?: string): void {
    this.sendEvent({
      event_type: 'user_interaction',
      session_id: this.sessionId,
      user_id: userId,
      company_id: companyId,
      event_data: {
        interaction_type: type,
        element,
        timestamp: new Date().toISOString()
      },
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Send event to backend analytics
   */
  private async sendEvent(event: ChatEvent): Promise<void> {
    try {
      // Only send if we have a session
      if (!event.session_id && event.event_type !== 'session_start') {
        return;
      }

      await fetch(getApiUrl('api/chat-analytics'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(event)
      });
    } catch (error) {
      // Silently fail - analytics shouldn't break the app
      console.warn('[Chat Analytics] Failed to send event:', error);
    }
  }

  /**
   * Generate unique session ID
   */
  private generateSessionId(): string {
    // Generate a proper UUID v4
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  /**
   * Get current session ID
   */
  getSessionId(): string | null {
    return this.sessionId;
  }

  /**
   * Get session stats
   */
  getSessionStats(): { messageCount: number; sessionDuration: number } {
    return {
      messageCount: this.messageCount,
      sessionDuration: this.sessionStartTime ? Date.now() - this.sessionStartTime : 0
    };
  }
}

// Export singleton instance
export const chatAnalytics = new ChatAnalytics();
export default chatAnalytics;
